import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import {
  parseNewChatCommand,
  requestStandaloneNewChat,
} from "@/lib/new-chat-command";
import { useInputBarTarget } from "@/stores/input-bar-target-store";

type SubmitOptions = NonNullable<
  Parameters<CursorInputContextValue["onSubmit"]>[2]
>;

/** The worker composer's forced native path (use-conversation-frame-submit.ts),
 *  with its own composer id so retry envelopes never mix with chat drafts. */
export function nativeChildSubmitOptions(
  conversationId: string,
): SubmitOptions & { conversationId: string; composerId: string } {
  return {
    conversationId,
    nativeChildInstruction: true,
    textOnly: true,
    // Literal text + no auto-capture, but user-attached images may flow;
    // the backend rejects them for providers without image follow-ups.
    allowImages: true,
    startNewConversation: false,
    ignoreInputBarSessionState: true,
    composerId: `native-child:${conversationId}`,
  };
}

type BarSubmitContext = Pick<CursorInputContextValue, "onSubmit"> &
  Partial<Pick<CursorInputContextValue, "attachedImages">>;
type BarSubmitOptions = Pick<
  SubmitOptions,
  "skipAutoCapture" | "screenRecordings" | "recordingImages" | "composerId"
>;

function hasAttachedMedia(
  ctx: BarSubmitContext,
  options?: BarSubmitOptions,
): boolean {
  return (
    [...(ctx.attachedImages ?? []), ...(options?.recordingImages ?? [])].some(
      (attachment) => attachment.imageId && !attachment.uploading,
    ) || Boolean(options?.screenRecordings?.clips.length)
  );
}

/** Send the standalone, compact or tray draft to its current target; a
 *  leading `@new` or `/new` first starts a new chat for the rest. Submitting
 *  is the one mechanism that drops a picked row's stale prediction — a
 *  sub-agent target never gets an accepted-runtime receipt, and a native
 *  child's messages may never reach the conversation store, so nothing
 *  else would clear it there. */
export function submitInputBarTarget(
  ctx: BarSubmitContext,
  value: string,
  agentKey?: string,
  options?: BarSubmitOptions,
): ReturnType<CursorInputContextValue["onSubmit"]> {
  const newChatRequest = parseNewChatCommand(value);
  if (newChatRequest !== null) {
    requestStandaloneNewChat();
    if (!newChatRequest && !hasAttachedMedia(ctx, options)) return true;
    value = newChatRequest;
  }
  const target = useInputBarTarget.getState().target;
  if (!isSubagentTarget(target)) {
    useInputBarTarget.getState().clearPendingNextPrompt();
    return options
      ? ctx.onSubmit(value, agentKey, options)
      : ctx.onSubmit(value, agentKey);
  }
  const text = value.trim();
  if (!text && !hasAttachedMedia(ctx, options)) return false;
  useInputBarTarget.getState().clearPendingNextPrompt();
  // The child's own composer id outranks the bar's.
  return ctx.onSubmit(text, "denker", {
    ...options,
    ...nativeChildSubmitOptions(target.conversationId),
  });
}
